import { inject, Injectable } from '@angular/core';
import { delay, Observable, of } from 'rxjs';
import {
  ApiService,
  GenericResponseCode,
  GenericResponseResult,
} from '@brighthub/api';
import { ClientSessionService, ClientSubjectData } from '@brighthub/uisession';
import {
  ClientProfilePayload,
  ClientProfileResponse,
  ClientProfileSaveSectionResponse,
  ClientProfileSection,
} from './client-profile.model';

// ---------------------------------------------------------------------------
// Deterministic seed helpers
// ---------------------------------------------------------------------------

/** djb2 hash — fast, unsigned 32-bit, no external deps. */
function djb2(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) + hash + str.charCodeAt(i)) >>> 0;
  }
  return hash;
}

/** Pick a stable value from `options` using `guid + key` as the seed. */
function seedPick<T>(guid: string, key: string, options: readonly T[]): T {
  return options[djb2(guid + key) % options.length];
}

/** Derive a stable N-digit numeric string from guid + key. */
function seedDigits(guid: string, key: string, digits: number): string {
  const max = Math.pow(10, digits);
  const min = Math.pow(10, digits - 1);
  return String(min + (djb2(guid + key) % (max - min)));
}

// ---------------------------------------------------------------------------
// Lookup tables
// ---------------------------------------------------------------------------

const FIRST_NAMES = [
  'James', 'Mary', 'Robert', 'Patricia', 'Michael', 'Jennifer', 'William',
  'Linda', 'David', 'Barbara', 'Richard', 'Susan', 'Joseph', 'Jessica',
  'Thomas', 'Sarah', 'Charles', 'Karen', 'Christopher', 'Lisa',
] as const;

const LAST_NAMES = [
  'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller',
  'Davis', 'Rodriguez', 'Martinez', 'Hernandez', 'Lopez', 'Gonzalez',
  'Wilson', 'Anderson', 'Thomas', 'Taylor', 'Moore', 'Jackson', 'Martin',
] as const;

const GENDERS = ['Male', 'Female', 'Non-binary'] as const;

const MARITAL_STATUSES = [
  'Single', 'Married', 'Domestic partner', 'Divorced', 'Widowed',
] as const;

const LANGUAGES = [
  'English', 'Spanish', 'French', 'Mandarin', 'Portuguese', 'Hindi',
] as const;

const COMPANIES = [
  'Apex Solutions', 'Blue Ridge Partners', 'Crestview Industries',
  'Dune Capital Group', 'Evergreen Consulting', 'Fairway Technologies',
  'Granite Financial', 'Harbor Wealth Advisors', 'Ironwood Group',
  'Juniper Analytics',
] as const;

const JOB_TITLES = [
  'Software Engineer', 'Senior Software Engineer', 'Product Manager',
  'Data Analyst', 'UX Designer', 'Financial Analyst', 'Operations Manager',
  'Marketing Specialist', 'HR Business Partner', 'Sales Representative',
  'Account Executive', 'Business Analyst', 'Project Manager',
  'Customer Success Manager', 'DevOps Engineer',
] as const;

const DEPARTMENTS = [
  'Engineering', 'Product', 'Design', 'Finance', 'Operations',
  'Marketing', 'Human Resources', 'Sales', 'Customer Success', 'Legal',
] as const;

const EMPLOYMENT_TYPES = ['Full-time', 'Part-time', 'Contract'] as const;

const SALARY_BANDS = [
  55000, 62000, 70000, 78000, 85000, 92000, 100000, 110000, 125000, 140000,
] as const;

const STREET_NUMBERS = [
  '12', '45', '100', '215', '320', '480', '512', '750', '888', '1024',
] as const;

const STREET_NAMES = [
  'Oak Street', 'Maple Avenue', 'Cedar Lane', 'Elm Drive', 'Pine Road',
  'Willow Way', 'River Blvd', 'Summit Court', 'Valley View', 'Harbor Walk',
] as const;

const CITIES = [
  'Atlanta', 'Austin', 'Boston', 'Charlotte', 'Chicago',
  'Dallas', 'Denver', 'Houston', 'Los Angeles', 'Miami',
  'Nashville', 'New York', 'Phoenix', 'Portland', 'Seattle',
] as const;

const STATES = [
  'GA', 'TX', 'MA', 'NC', 'IL',
  'TX', 'CO', 'TX', 'CA', 'FL',
  'TN', 'NY', 'AZ', 'OR', 'WA',
] as const;

const STATE_TIMEZONES: Record<string, string> = {
  GA: 'America/New_York', TX: 'America/Chicago', MA: 'America/New_York',
  NC: 'America/New_York', IL: 'America/Chicago', CO: 'America/Denver',
  CA: 'America/Los_Angeles', FL: 'America/New_York', TN: 'America/Chicago',
  NY: 'America/New_York', AZ: 'America/Phoenix', OR: 'America/Los_Angeles',
  WA: 'America/Los_Angeles',
};

const EMAIL_DOMAINS = [
  'gmail.com', 'yahoo.com', 'outlook.com', 'icloud.com', 'protonmail.com',
] as const;

const COMPANY_DOMAINS = [
  'apexsolutions.com', 'blueridgepartners.com', 'crestviewind.com',
  'dunecapital.com', 'evergreenco.com', 'fairwaytech.com',
  'granitefinancial.com', 'harborwealth.com', 'ironwoodgroup.com',
  'juniperana.com',
] as const;

const HEALTH_PLANS = [
  'PPO Gold', 'PPO Silver', 'HMO Standard', 'HDHP + HSA', 'EPO Plus',
] as const;

const RETIREMENT_RATES = ['3%', '4%', '5%', '6%', '8%', '10%'] as const;

const LIFE_INSURANCE = ['1x salary', '2x salary', '3x salary'] as const;

const ENROLLED_OPTIONS = ['Enrolled', 'Not enrolled', 'Waived'] as const;

const PTO_BALANCES = [8, 10, 12, 15, 18, 20, 22, 25] as const;

const RELATIONSHIPS = ['spouse', 'parent', 'sibling', 'domestic partner'] as const;

const NOTES_SNIPPETS = [
  'Prefers morning meetings. Strong communicator.',
  'Key contributor on recent platform migration.',
  'Works remotely full-time. Responsive via email.',
  'Recently promoted. High engagement scores.',
  'Interested in leadership development opportunities.',
  'Flexible schedule — core hours 10am–3pm.',
  'Bilingual; comfortable in Spanish and English.',
] as const;

// ---------------------------------------------------------------------------
// Mock payload builder
// ---------------------------------------------------------------------------

/**
 * Build a mock profile payload derived from real session subject data.
 *
 * Priority: real subjectData value → deterministic seed from guid → never blank.
 * Every field uses the same pattern so adding/removing real API fields later
 * requires only removing the seed fallback, not restructuring the function.
 *
 * Replace with a real API call when the backend is ready:
 *   return this._api.get<ClientProfileResponse>('api-mobile', `/clients/${guid}/profile`);
 */
function mockProfilePayload(s: ClientSubjectData): ClientProfilePayload {
  const g = s.guid;

  // Resolved names (used in derived fields like email)
  const firstName = s.firstName || seedPick(g, 'firstName', FIRST_NAMES);
  const lastName = s.lastName || seedPick(g, 'lastName', LAST_NAMES);
  const company = s.company || seedPick(g, 'company', COMPANIES);
  const city = s.city || seedPick(g, 'city', CITIES);
  const stateAbbr = s.state || seedPick(g, 'state', STATES);
  const companyDomain = seedPick(g, 'companyDomain', COMPANY_DOMAINS);

  const firstNameLower = firstName.toLowerCase();
  const lastNameLower = lastName.toLowerCase();

  const sections: ClientProfileSection[] = [
    {
      id: 'personal',
      title: 'Personal',
      items: [
        { label: 'First name', value: firstName, editable: true, key: 'firstName' },
        { label: 'Last name', value: lastName, editable: true, key: 'lastName' },
        {
          label: 'Date of birth',
          value: s.dateOfBirth || (() => {
            const year = 1960 + (djb2(g + 'dobYear') % 35);
            const month = String(1 + (djb2(g + 'dobMonth') % 12)).padStart(2, '0');
            const day = String(1 + (djb2(g + 'dobDay') % 28)).padStart(2, '0');
            return `${year}-${month}-${day}`;
          })(),
          editable: false,
          key: 'dateOfBirth',
        },
        {
          label: 'Gender',
          value: s.gender || seedPick(g, 'gender', GENDERS),
          editable: false,
          key: 'gender',
        },
        {
          label: 'Marital status',
          value: seedPick(g, 'maritalStatus', MARITAL_STATUSES),
          editable: true,
          key: 'maritalStatus',
        },
        {
          label: 'Preferred language',
          value: seedPick(g, 'preferredLanguage', LANGUAGES),
          editable: true,
          key: 'preferredLanguage',
        },
        {
          label: 'SSN (last 4)',
          value: `••••${seedDigits(g, 'ssn', 4)}`,
          editable: false,
          key: 'ssnLast4',
        },
      ],
    },
    {
      id: 'employment',
      title: 'Employment',
      items: [
        { label: 'Company', value: company, editable: true, key: 'company' },
        {
          label: 'Job title',
          value: seedPick(g, 'jobTitle', JOB_TITLES),
          editable: true,
          key: 'jobTitle',
        },
        {
          label: 'Department',
          value: seedPick(g, 'department', DEPARTMENTS),
          editable: true,
          key: 'department',
        },
        {
          label: 'Employment type',
          value: s.employmentType || seedPick(g, 'employmentType', EMPLOYMENT_TYPES),
          editable: false,
          key: 'employmentType',
        },
        {
          label: 'Hire date',
          value: s.hireDate || (() => {
            const year = 2010 + (djb2(g + 'hireYear') % 14);
            const month = String(1 + (djb2(g + 'hireMonth') % 12)).padStart(2, '0');
            const day = String(1 + (djb2(g + 'hireDay') % 28)).padStart(2, '0');
            return `${year}-${month}-${day}`;
          })(),
          editable: false,
          key: 'hireDate',
        },
        {
          label: 'Annual pay',
          value: s.annualPay ?? seedPick(g, 'annualPay', SALARY_BANDS),
          editable: true,
          key: 'annualPay',
        },
        {
          label: 'Employee ID',
          value: `EMP-${seedDigits(g, 'employeeId', 4)}`,
          editable: false,
          key: 'employeeId',
        },
        {
          label: 'Manager',
          value: `${seedPick(g, 'managerFirst', FIRST_NAMES)} ${seedPick(g, 'managerLast', LAST_NAMES)}`,
          editable: false,
          key: 'manager',
        },
      ],
    },
    {
      id: 'location',
      title: 'Location',
      items: [
        {
          label: 'Street',
          value: `${seedPick(g, 'streetNum', STREET_NUMBERS)} ${seedPick(g, 'streetName', STREET_NAMES)}`,
          editable: true,
          key: 'street',
        },
        { label: 'City', value: city, editable: true, key: 'city' },
        { label: 'State', value: stateAbbr, editable: true, key: 'state' },
        {
          label: 'ZIP code',
          value: seedDigits(g, 'zipCode', 5),
          editable: true,
          key: 'zipCode',
        },
        { label: 'Country', value: 'United States', editable: false, key: 'country' },
        {
          label: 'Time zone',
          value: STATE_TIMEZONES[stateAbbr] ?? 'America/Chicago',
          editable: false,
          key: 'timeZone',
        },
      ],
    },
    {
      id: 'contact',
      title: 'Contact',
      items: [
        {
          label: 'Work phone',
          value: `(${seedDigits(g, 'workPhoneArea', 3)}) ${seedDigits(g, 'workPhonePre', 3)}-${seedDigits(g, 'workPhoneLine', 4)}`,
          editable: true,
          key: 'workPhone',
        },
        {
          label: 'Mobile phone',
          value: `(${seedDigits(g, 'mobileArea', 3)}) ${seedDigits(g, 'mobilePre', 3)}-${seedDigits(g, 'mobileLine', 4)}`,
          editable: true,
          key: 'mobilePhone',
        },
        {
          label: 'Personal email',
          value: `${firstNameLower}.${lastNameLower}@${seedPick(g, 'emailDomain', EMAIL_DOMAINS)}`,
          editable: true,
          key: 'personalEmail',
        },
        {
          label: 'Work email',
          value: `${firstNameLower}.${lastNameLower}@${companyDomain}`,
          editable: false,
          key: 'workEmail',
        },
        {
          label: 'Emergency contact',
          value: `${seedPick(g, 'ecFirst', FIRST_NAMES)} ${seedPick(g, 'ecLast', LAST_NAMES)} (${seedPick(g, 'ecRelationship', RELATIONSHIPS)})`,
          editable: true,
          key: 'emergencyContact',
        },
      ],
    },
    {
      id: 'benefits',
      title: 'Benefits',
      items: [
        {
          label: 'Health plan',
          value: seedPick(g, 'healthPlan', HEALTH_PLANS),
          editable: false,
          key: 'healthPlan',
        },
        {
          label: '401(k) enrollment',
          value: `Enrolled — ${seedPick(g, 'retirementRate', RETIREMENT_RATES)}`,
          editable: false,
          key: 'retirementEnrollment',
        },
        {
          label: 'PTO balance',
          value: seedPick(g, 'ptoBalance', PTO_BALANCES),
          editable: false,
          key: 'ptoBalance',
        },
        {
          label: 'Life insurance',
          value: seedPick(g, 'lifeInsurance', LIFE_INSURANCE),
          editable: false,
          key: 'lifeInsurance',
        },
        {
          label: 'Dental',
          value: seedPick(g, 'dental', ENROLLED_OPTIONS),
          editable: false,
          key: 'dental',
        },
        {
          label: 'Vision',
          value: seedPick(g, 'vision', ENROLLED_OPTIONS),
          editable: false,
          key: 'vision',
        },
      ],
    },
    {
      id: 'notes',
      title: 'Notes',
      items: [
        {
          label: 'Internal notes',
          value: seedPick(g, 'internalNotes', NOTES_SNIPPETS),
          editable: true,
          key: 'internalNotes',
        },
        { label: 'Accommodations', value: '', editable: true, key: 'accommodations' },
      ],
    },
  ];
  return { sections };
}

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

/**
 * Fetches client profile (sections + items with editable flags) for the given GUID.
 * Response shape matches API style: result (code/status/message) + payload.
 *
 * Currently mocked: derives values from the session's subjectData so the UI
 * reflects the real selected client. Replace mock with real API call when ready:
 *   return this._api.get<ClientProfileResponse>('api-mobile', `/clients/${guid}/profile`);
 */
@Injectable({
  providedIn: 'root',
})
export class ClientProfileService {
  private _api = inject(ApiService);
  private _clientSession = inject(ClientSessionService);

  getProfile(guid: string): Observable<ClientProfileResponse & GenericResponseResult> {
    if (!guid?.trim()) {
      return of({
        result: {
          status: 400,
          code: GenericResponseCode.MISSING_REQ_FIELD,
          message: 'GUID is required',
          suggestion: '',
          suggestionCopyKey: '',
          docs: '',
        },
        payload: { sections: [] },
      });
    }

    const subjectData = this._clientSession.selectedSession()?.subjectData;
    if (!subjectData) {
      return of({
        result: {
          status: 404,
          code: GenericResponseCode.SERVICE_ERROR,
          message: 'No subject data available for selected client',
          suggestion: '',
          suggestionCopyKey: '',
          docs: '',
        },
        payload: { sections: [] },
      });
    }

    return of({
      result: {
        status: 200,
        code: GenericResponseCode.SERVICE_OK,
        message: '',
        suggestion: '',
        suggestionCopyKey: '',
        docs: '',
      },
      payload: mockProfilePayload(subjectData),
    }).pipe(delay(400));
  }

  /**
   * Save edited values for a single profile section.
   * Mock: simulates API call; replace with ApiService.patch when backend is ready.
   */
  saveProfileSection(
    guid: string,
    sectionId: string,
    values: Record<string, string | number>
  ): Observable<ClientProfileSaveSectionResponse & GenericResponseResult> {
    const savedKeys = Object.keys(values);
    return of({
      result: {
        status: 200,
        code: GenericResponseCode.SERVICE_OK,
        message: '',
        suggestion: '',
        suggestionCopyKey: '',
        docs: '',
      },
      payload: { sectionId, savedKeys },
    }).pipe(delay(600));
  }
}
